/**
 * Marketing Content Constants
 * Centralized text content for landing pages, auth, and marketing materials
 * Update content here and it will reflect everywhere
 */

import { BRAND } from './brand';

// Hero Section Content
export const HERO_CONTENT = {
  tagline: BRAND.FULL_TAGLINE,
  headline: 'Your home and family,',
  headlineHighlight: 'organized',
  subheadline: 'Keep addresses, documents, vehicles and important dates in one place — with reminders when you need them.',
  ctaButton: 'Start for free',
};

// Trust Badges
export const TRUST_BADGES = {
  encryption: 'AES-256 encrypted',
  privacy: 'Your data stays private',
  freePlan: 'Free forever plan',
};

// Features Section Content
export const FEATURES_SECTION = {
  title: 'Everything in one place',
  subtitle: 'Addresses, insurance, IDs, vehicles, and contacts — secure, organized, and always up to date',
};

// Feature Cards Content
export const FEATURE_CARDS = {
  address: {
    title: 'Address Management',
    description: 'Store all important addresses with complete contact information. Keep service providers, utilities, emergency contacts, and property details organized and accessible.',
    gradient: 'from-indigo-500 to-indigo-600',
    shadowColor: 'shadow-indigo-500/20',
    hoverBorder: 'hover:border-indigo-200',
  },
  vehicles: {
    title: 'Vehicle Information',
    description: 'Keep vehicle details and service centers in one place. Track important dates and set reminders for renewals.',
    gradient: 'from-cyan-500 to-cyan-600',
    shadowColor: 'shadow-cyan-500/20',
    hoverBorder: 'hover:border-cyan-200',
  },
  healthcare: {
    title: 'Healthcare Information',
    description: 'Store doctors, hospitals, pharmacies, and labs with their contact details. Keep your family\'s medical information organized and accessible.',
    gradient: 'from-pink-500 to-pink-600',
    shadowColor: 'shadow-pink-500/20',
    hoverBorder: 'hover:border-pink-200',
  },
  insurance: {
    title: 'Insurance Tracking',
    description: 'Manage all insurance policies, local agents, and contact information. Track renewal dates and important policy details with automated reminders.',
    gradient: 'from-blue-500 to-blue-600',
    shadowColor: 'shadow-blue-500/20',
    hoverBorder: 'hover:border-blue-200',
  },
  financial: {
    title: 'Finance',
    description: 'Store financial references, policy details, and related information. Keep your family\'s financial information organized and accessible.',
    gradient: 'from-emerald-500 to-emerald-600',
    shadowColor: 'shadow-emerald-500/20',
    hoverBorder: 'hover:border-emerald-200',
  },
  calendar: {
    title: 'Family Calendar',
    description: 'Track reminders, maintenance schedules, and important dates across the family. Share and sync seamlessly — keeping everyone on the same page.',
    gradient: 'from-purple-500 to-purple-600',
    shadowColor: 'shadow-purple-500/20',
    hoverBorder: 'hover:border-purple-200',
  },
  reminders: {
    title: 'Reminder Bundles',
    description: 'Add multiple reminders instantly — all in one click. Choose from Homeowner, Renter, or Vehicle Owner bundles to track payments, renewals, and maintenance effortlessly.',
    gradient: 'from-orange-500 to-orange-600',
    shadowColor: 'shadow-orange-500/20',
    hoverBorder: 'hover:border-orange-200',
  },
  familySharing: {
    title: 'Family Sharing',
    description: 'Invite family members via Google Sign-in. Share addresses, doctors, financial info, insurance, and important contacts — keeping everyone informed and prepared.',
    gradient: 'from-green-500 to-green-600',
    shadowColor: 'shadow-green-500/20',
    hoverBorder: 'hover:border-green-200',
  },
  sharingBundles: {
    title: 'Sharing Bundles',
    description: 'Share trusted home contacts, utilities, quick links, and services with family, neighbors, or friends — securely and view-only.',
    gradient: 'from-violet-500 to-violet-600',
    shadowColor: 'shadow-violet-500/20',
    hoverBorder: 'hover:border-violet-200',
  },
  quickLinks: {
    title: 'Smart Quick Links',
    description: 'Automatically finds trusted service and government links related to your addresses. Property tax portals, HOA sites, utility payment pages, and more — all in one place.',
    gradient: 'from-sky-500 to-sky-600',
    shadowColor: 'shadow-sky-500/20',
    hoverBorder: 'hover:border-sky-200',
  },
};

// Feature list as array for easy iteration
export const FEATURE_LIST = [
  { key: 'address', icon: 'Home', ...FEATURE_CARDS.address },
  { key: 'familySharing', icon: 'Users', ...FEATURE_CARDS.familySharing },
  { key: 'financial', icon: 'Wallet', ...FEATURE_CARDS.financial },
  { key: 'healthcare', icon: 'Heart', ...FEATURE_CARDS.healthcare },
  { key: 'insurance', icon: 'Shield', ...FEATURE_CARDS.insurance },
  { key: 'calendar', icon: 'Calendar', ...FEATURE_CARDS.calendar },
  { key: 'reminders', icon: 'Bell', ...FEATURE_CARDS.reminders },
  { key: 'vehicles', icon: 'Car', ...FEATURE_CARDS.vehicles },
  { key: 'sharingBundles', icon: 'Link', ...FEATURE_CARDS.sharingBundles },
];


// ── Pro Storage Features — Google Drive marketing ─────────────────

export const PRO_STORAGE_FEATURES = {
  title: 'Pro: Your Data, Your Storage',
  subtitle: 'Store everything in YOUR Google Drive — browse photos and documents anytime',
  features: [
    {
      icon: 'HardDrive',
      title: 'Your Google Drive',
      description: 'All content stored in your personal Google Drive. We never hold your data.',
    },
    {
      icon: 'FolderOpen',
      title: 'Organized Folders',
      description: 'Browse Addresses, Vehicles, Insurance folders right in Google Drive.',
    },
    {
      icon: 'Image',
      title: 'View Photos Anytime',
      description: 'Photos are regular image files — view, share, or download from Drive.',
    },
    {
      icon: 'FileText',
      title: 'Access Documents',
      description: 'PDFs, documents accessible directly in Google Drive without the app.',
    },
    {
      icon: 'Lock',
      title: 'Encrypted Metadata',
      description: 'Sensitive data (passwords, account numbers) encrypted with AES-256.',
    },
    {
      icon: 'Infinity',
      title: 'Unlimited Storage',
      description: 'Uses your Google Drive quota — most users have 15GB+ free.',
    },
  ],
  folderStructure: `
LucidDock/
├── Addresses/
│   └── 123 Main Street/
│       ├── Photos/
│       └── Documents/
├── Vehicles/
│   └── 2023 Toyota Camry/
│       ├── Photos/
│       └── Documents/
├── Insurance/
├── Healthcare/
└── Financial/
  `,
};

// ── Feedback Sheet — emoji reactions + optional text ─────────────────

export const FEEDBACK = {
  TITLE: 'Feedback',
  RATE_LABEL: 'Rate your experience',
  MESSAGE_LABEL: "What's on your mind?",
  MESSAGE_MAX_LENGTH: 500,
  SUBMIT_LABEL: 'Send',
  SUCCESS_MESSAGE: 'Thanks for your feedback!',
  ERROR_MESSAGE: 'Failed to send feedback. Please try again.',
  EMOJI_OPTIONS: [
    { emoji: '😍', label: 'Love it' },
    { emoji: '😊', label: 'Good' },
    { emoji: '😐', label: 'Okay' },
    { emoji: '😟', label: 'Not great' },
  ],
} as const;

// ── Upgrade Banner — shown to free users ────────────────────────────

export const UPGRADE_BANNER = {
  message: 'Unlock unlimited addresses, family sharing, and more.',
  buttonLabel: 'Upgrade',
} as const;

// ── Plan Features — Single Source of Truth ──────────────────────────
// Prices derived from PLAN_PRICES. All plan data lives here.
// Import and use everywhere — no hardcoding in components.

import { getPlanPriceDisplay, getAnnualPriceDisplay, getOriginalPriceDisplay, getEarlyBirdSavingsPercent, type SubscriptionPlan } from './planLimits';

/** Landing page feature list item — highlight = bold + blue icon */
interface PlanFeatureItem {
  text: string;
  highlight: boolean;
}

/** Modal feature table row — value is string (displayed) or true (checkmark) */
interface PlanFeatureRow {
  label: string;
  value: string | true;
}

/** Plan display metadata */
interface PlanMeta {
  title: string;
  subtitle: string;
  defaultBadge: string;
}

export const PLAN_FEATURES = {
  free: {
    name: 'Free',
    price: getPlanPriceDisplay('free'),
    priceYearly: undefined as string | undefined,
    storage: '200 MB',
    storageType: 'Cloud',
    /** Landing page feature list */
    features: [
      { text: '5 addresses', highlight: false },
      { text: '10 personal items total', highlight: false },
      { text: '10 attachments total, up to 200 MB', highlight: false },
      { text: '1 user (Owner)', highlight: false },
      { text: '1 active share bundle', highlight: false },
      { text: 'Smart reminders', highlight: true },
      { text: 'AI Quick Links', highlight: true },
    ] as PlanFeatureItem[],
    /** Modal comparison table rows */
    featureRows: [
      { label: 'Addresses', value: '5' },
      { label: 'Personal items', value: '10' },
      { label: 'Users', value: '1 (Owner)' },
      { label: 'Attachments', value: '10 total' },
      { label: 'Share bundles', value: '1' },
      { label: 'AI Quick Links', value: true },
    ] as PlanFeatureRow[],
  },
  pro: {
    name: 'Pro',
    price: getPlanPriceDisplay('pro'),
    priceYearly: getAnnualPriceDisplay('pro'),
    originalPrice: getOriginalPriceDisplay('pro'),
    savingsPercent: getEarlyBirdSavingsPercent('pro'),
    storage: 'Google Drive',
    storageType: 'Google Drive',
    features: [
      { text: 'Everything in Free', highlight: false },
      { text: 'Unlimited addresses & personal items', highlight: true },
      { text: 'Unlimited photos & documents', highlight: true },
      { text: 'Family members (Editor + Viewer)', highlight: true },
      { text: 'Unlimited sharing', highlight: true },
      { text: 'LucidDock Cloud or Google Drive', highlight: true },
    ] as PlanFeatureItem[],
    featureRows: [
      { label: 'Storage', value: 'Google Drive' },
      { label: 'Addresses', value: 'Unlimited' },
      { label: 'Users', value: 'Unlimited' },
      { label: 'Sharing', value: 'Unlimited' },
      { label: 'Photos & documents', value: 'Unlimited' },
      { label: 'AI Quick Links', value: true },
    ] as PlanFeatureRow[],
  },
  /** Legacy alias — identical to pro. Existing code may reference PLAN_FEATURES.premium. */
  get premium() { return this.pro; },
};

/** Plan display metadata — used in Manage Plan modal and plan cards */
export const PLAN_META: Record<SubscriptionPlan, PlanMeta> = {
  free: { title: 'Free', subtitle: '5 addresses, 10 personal items', defaultBadge: '' },
  pro: { title: 'Pro', subtitle: 'Unlimited everything', defaultBadge: '' },
  premium: { title: 'Pro', subtitle: 'Unlimited everything', defaultBadge: '' },
};
